import moment from "moment";
import React, { FC, useState } from "react";
import { LuFileText, LuPlus } from "react-icons/lu";
import { useNavigate } from "react-router-dom";

import { Card } from "../../../../../../components/card/Card";
import { getInitialsFromFullName } from "../../../../../../constants/constants";
import { WorkDetailsModel } from "../../../../../../factories/work/model/work-details.model";
import { useGetWorkBillingQuery } from "../../../../../../services/BillingService";
import { formatMoney } from "../../../../../billing/invoices/form/invoice-form.utils";
import { hoursLabel } from "../../../../../billing/invoices/form/invoiceWork.utils";
import { workInvoicePayLabel } from "../../../../../billing/ui/invoiceStatus.utils";

type Props = {
  work: WorkDetailsModel;
};

const SHOWN = 4;

/**
 * Work item Summary "Billing" box (owner, 2026-10-21): invoices with lines for
 * this work item, billed / paid / unbilled time, and an "Invoice" button.
 * Only for Admins / "View Billings"; the API answers 403 to others, and then
 * the box isn't shown.
 */
export const WorkBillingCard: FC<Props> = ({ work }) => {
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(false);
  const { data, isError } = useGetWorkBillingQuery(work.id, {
    skip: !work.id,
    refetchOnMountOrArgChange: true,
  });

  if (!data || isError) return null;

  const invoices = showAll ? data.invoices : data.invoices.slice(0, SHOWN);
  const more = data.invoices.length - invoices.length;
  const customerId = work.customerId || data.customerId || "";
  const clientName = (work.customer?.name || "").trim();

  const stat = (label: string, value: string, hint?: string) => (
    <div className={"min-w-0 rounded-lg bg-[#F8F9FB] px-2.5 py-2"}>
      <div className={"text-[11px] font-semibold text-[#8A90A0]"}>{label}</div>
      <div
        className={"truncate text-[14px] font-bold text-[#1F2433]"}
        title={hint}
      >
        {value}
      </div>
    </div>
  );

  return (
    <Card extraClasses={"shadow-box mt-6 max-w-full px-4"}>
      <div>
        <div className={"flex items-center justify-between pt-4"}>
          <div className={"text-base font-semibold"}>Billing</div>
          <button
            type={"button"}
            onClick={() =>
              navigate(
                `/billing/invoices/add?customerId=${encodeURIComponent(
                  customerId,
                )}&workId=${encodeURIComponent(work.id)}`,
                {
                  // Pre-selects the client even when it isn't in the first
                  // page of the client list (same as the client page does).
                  state: clientName
                    ? {
                        defaultCustomer: {
                          value: customerId,
                          label: clientName,
                          avatar: getInitialsFromFullName(clientName),
                          avatarColor: work.customer?.businessContactId
                            ? "purple"
                            : "blue",
                        },
                      }
                    : undefined,
                },
              )
            }
            className={
              "inline-flex h-8 items-center gap-1.5 rounded-lg bg-[#6B4EFF] px-3 text-[12.5px] font-bold text-white hover:bg-[#5A3EEB]"
            }
          >
            <LuPlus size={14} />
            Invoice
          </button>
        </div>

        <div className={"mt-3 grid grid-cols-3 gap-2"}>
          {stat("Billed", `$${formatMoney(data.billed)}`)}
          {stat("Paid", `$${formatMoney(data.paid)}`)}
          {stat(
            "Unbilled time",
            hoursLabel(data.unbilledSeconds),
            "Billable time on this work item that isn't on an invoice yet",
          )}
        </div>

        <div className={"mt-3 pb-4"}>
          {!data.invoices.length ? (
            <div className={"py-2 text-[13px] text-[#8A90A0]"}>
              No invoices for this work item yet.
            </div>
          ) : (
            <>
              {invoices.map((invoice) => (
                <button
                  key={invoice.id}
                  type={"button"}
                  onClick={() => navigate(`/billing/invoices/${invoice.id}`)}
                  className={
                    "flex w-full items-center gap-2.5 border-b border-[#F1F2F5] py-2.5 text-left last:border-b-0 hover:bg-[#FAFAFC]"
                  }
                >
                  <LuFileText size={16} className={"shrink-0 text-[#9097A8]"} />
                  <div className={"min-w-0 flex-1"}>
                    <div className={"text-[13px] font-semibold text-[#1F2433]"}>
                      Invoice #{invoice.invoiceNo}
                    </div>
                    <div className={"truncate text-[11.5px] text-[#8A90A0]"}>
                      {invoice.invoiceDate
                        ? moment(invoice.invoiceDate).format("MMM D, YYYY")
                        : ""}
                      {invoice.workAmount !== invoice.total &&
                        ` · $${formatMoney(
                          invoice.workAmount,
                        )} of $${formatMoney(invoice.total)}`}
                    </div>
                  </div>
                  <div className={"shrink-0 text-right"}>
                    <div
                      className={
                        "text-[13px] font-semibold tabular-nums text-[#1F2433]"
                      }
                    >
                      ${formatMoney(invoice.workAmount)}
                    </div>
                    <div
                      className={
                        workInvoicePayLabel(invoice) === "Paid"
                          ? "text-[11.5px] font-bold text-[#15803D]"
                          : "text-[11.5px] font-bold text-[#B45309]"
                      }
                    >
                      {workInvoicePayLabel(invoice)}
                    </div>
                  </div>
                </button>
              ))}
              {more > 0 && (
                <button
                  type={"button"}
                  onClick={() => setShowAll(true)}
                  className={
                    "mt-1 text-[12.5px] font-semibold text-[#6B4EFF] hover:underline"
                  }
                >
                  Show {more} more
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </Card>
  );
};
